import { Schema } from "effect";
import { z } from "zod";
import { BboxCodec, type Bbox } from "./bbox.js";

/**
 * Client Protocol — one wire contract for CLI, desktop, and web (S.5.8).
 *
 * The product is a server. Every UI is a client of that server.
 * That is the OpenCode lesson (`opencode serve`; the TUI is just the first
 * client) applied to okraPDF.
 *
 * Canonical sources, in priority order:
 *   1. github.com/okrapdf/cli — parse/provider/parser/artifact shapes
 *      (`DESIGN.md` seams + `src/core/blocks.ts`)
 *   2. github.com/anomalyco/opencode — HTTP + SSE attachable serve
 *   3. github.com/okrapdf/desktop — read-before-parse + page lifecycle
 *
 * S.5.7 (`internal/specs/okra-server-protocol.md`) remains the hosted `/v1`
 * document/job/upload profile. This module is the workbench profile those
 * same clients use for local parse, provider setup, and run attach. A host
 * may implement one or both; clients degrade on advertised capabilities.
 */

export const CLIENT_PROTOCOL_VERSION = "okra.client.v1" as const;
export const CLIENT_PROTOCOL_HEADER = "x-okra-client-protocol" as const;

export type ClientProtocolVersion = typeof CLIENT_PROTOCOL_VERSION;

// ---------------------------------------------------------------------------
// Surfaces — who speaks the protocol
// ---------------------------------------------------------------------------

export const clientSurfaceSchema = z.enum(["cli", "desktop", "web"]);
export type ClientSurface = z.infer<typeof clientSurfaceSchema>;

export const clientHostSchema = z.enum([
  "cli_serve",
  "desktop_loopback",
  "cloud_v1",
  "self_host_v1",
]);
export type ClientHost = z.infer<typeof clientHostSchema>;

/**
 * What each surface is allowed to own. Anything else is a second protocol
 * and is the thing this contract exists to delete.
 */
export const CLIENT_SURFACE_SCOPES = z
  .array(
    z.object({
      surface: clientSurfaceSchema,
      role: z.enum(["server", "client", "both"]),
      owns: z.array(z.string().min(1)).min(1),
      mustNotOwn: z.array(z.string().min(1)).min(1),
      today: z.string().min(1),
      target: z.string().min(1),
    }),
  )
  .parse([
    {
      surface: "cli",
      role: "both",
      owns: [
        "Provider catalog and key resolution (flag > env > config).",
        "Parser seam and default layout-vlm implementation.",
        "Canonical parse artifacts (markdown, blocks, manifest).",
        "`okra serve` — the OpenCode-shaped HTTP + SSE host.",
        "`okra parse` as a client of that host, not a private code path.",
      ],
      mustNotOwn: [
        "A desktop-only result.json schema.",
        "A web-only RTK/fetch parse shape.",
        "Cloud account auth on the core parse path.",
      ],
      today:
        "github.com/okrapdf/cli is a one-shot `okra parse` with no serve. The monorepo `@okrapdf/cli` is a different cloud client.",
      target:
        "Standalone CLI grows `okra serve`. Desktop and web attach. Cloud `okra cloud` stays an opt-in namespace.",
    },
    {
      surface: "desktop",
      role: "both",
      owns: [
        "Native reader chrome (PDFKit on macOS, PDF.js on Windows).",
        "Read-before-parse: opening a PDF never starts a run.",
        "Page-lifecycle presentation and cancel/resume UX.",
        "Local plugin setup UI (Dots, Vision, Chandra, Ollama, Presidio).",
        "An authenticated app-owned loopback host for thin local clients.",
      ],
      mustNotOwn: [
        "A private StructuredExtractionDocument as the public contract.",
        "A Windows-only `/api/*` catalog that other clients cannot speak.",
        "In-process parser result types that CLI and web cannot import.",
        "A non-loopback listener or unauthenticated local mutation surface.",
      ],
      today:
        "macOS owns an authenticated random-port loopback host for its bundled thin CLI. Windows has an Ollama-shaped Go loopback (`GET /api/health`, `/api/providers`, `/api/runs`) with a different path catalog and a different result.json.",
      target:
        "Both desktop apps host CLIENT_PROTOCOL_ENDPOINTS for local clients while their native UIs stay attached to the same run state; wire shapes come from this module.",
    },
    {
      surface: "web",
      role: "client",
      owns: [
        "Hosted Studio / tools chrome and marketing.",
        "Attaching to a local `okra serve` or a cloud/self-host backend.",
        "Projecting protocol events into the existing design system.",
      ],
      mustNotOwn: [
        "Per-tool fetch + poll loops that invent a second parse contract.",
        "A third bbox dialect that is not converted through this module.",
        "A control-plane-only view of local parse (that protocol is hosted agents).",
      ],
      today:
        "Studio and `/tools/*` talk to Cloudflare routes and in-browser tools. Parse/extract shapes are per-runner.",
      target:
        "Web parse/extract/run attach uses this catalog. Hosted `/v1` stays the S.5.7 profile; workbench verbs do not grow a fourth client.",
    },
  ]);

// ---------------------------------------------------------------------------
// Neighbor protocols — keep them from collapsing into one pile
// ---------------------------------------------------------------------------

export const clientProtocolNeighborIdSchema = z.enum([
  "server_v1",
  "control_plane",
  "opencode_dispatch",
  "live_html",
]);
export type ClientProtocolNeighborId = z.infer<typeof clientProtocolNeighborIdSchema>;

export const CLIENT_PROTOCOL_NEIGHBORS = z
  .array(
    z.object({
      id: clientProtocolNeighborIdSchema,
      module: z.string().min(1),
      owns: z.string().min(1),
      relationship: z.string().min(1),
    }),
  )
  .parse([
    {
      id: "server_v1",
      module: "@okrapdf/schemas plus internal/specs/okra-server-protocol.md",
      owns: "Hosted `/v1` documents, upload, jobs, meta, SSE/WS events.",
      relationship:
        "A cloud or self-host backend implements S.5.7. The same process MAY also expose this workbench profile. Clients never sniff implementation; they read capabilities.",
    },
    {
      id: "control_plane",
      module: "@okrapdf/schemas/control-plane-protocol",
      owns: "Hosted agent session commands and replay (JOIN_SESSION, AGENT_MESSAGE, chat stream).",
      relationship:
        "Not the local parse/run contract. Desktop's assistant is a deterministic router, not this session bus.",
    },
    {
      id: "opencode_dispatch",
      module: "@okrapdf/schemas/opencode-dispatch",
      owns: "Foreign OpenCode `/session/:id/message` parse rules for iOS/desktop chat bridges.",
      relationship:
        "An adapter to someone else's serve. This module is okraPDF's own serve. Do not widen opencode-dispatch to carry parse blocks.",
    },
    {
      id: "live_html",
      module: "@okrapdf/schemas/live-html",
      owns: "Progressive PDF→HTML twin (`okra.live_html.v1`).",
      relationship:
        "A published-page contract, not a parse-run contract. `okra view` keeps live-html; `okra parse` / desktop Extract keep this module.",
    },
  ]);

// ---------------------------------------------------------------------------
// Rules the three surfaces share
// ---------------------------------------------------------------------------

export const clientProtocolRuleIdSchema = z.enum([
  "product_is_a_server",
  "cli_owns_wire_shapes",
  "read_before_parse",
  "providers_are_data_rows",
  "parsers_are_peers",
  "one_artifact_triple",
  "event_driven_ui",
  "bbox_converts_explicitly",
  "no_cloud_on_core_parse",
]);
export type ClientProtocolRuleId = z.infer<typeof clientProtocolRuleIdSchema>;

export const CLIENT_PROTOCOL_RULES = z
  .array(
    z.object({
      id: clientProtocolRuleIdSchema,
      summary: z.string().min(1),
      source: z.string().min(1),
    }),
  )
  .parse([
    {
      id: "product_is_a_server",
      summary:
        "CLI, desktop, and web attach to one HTTP + SSE host. Packaging (npm bin, Swift app, Worker) is distribution, never a second API.",
      source: "anomalyco/opencode serve; Ollama OLLAMA_HOST",
    },
    {
      id: "cli_owns_wire_shapes",
      summary:
        "Parse results, providers, and parsers are defined here from okrapdf/cli. Desktop and web project into these types; they do not publish competing schemas.",
      source: "okrapdf/cli DESIGN.md seams + src/core/blocks.ts",
    },
    {
      id: "read_before_parse",
      summary:
        "Registering or opening a document never starts a run. Only POST /document/:id/parse (or `okra parse`) may.",
      source: "okrapdf/desktop CLAUDE.md product boundary",
    },
    {
      id: "providers_are_data_rows",
      summary:
        "A provider is a catalog row + auth resolution + one of two transports (`gemini` | `openai-chat`). Adding a provider is data, not a class.",
      source: "okrapdf/cli providers/registry.ts (OpenCode model)",
    },
    {
      id: "parsers_are_peers",
      summary:
        "layout-vlm is the default parser, not the core. text-layer, docling-serve, Apple Vision, Dots, Chandra, and Ollama drop in behind ParserSpec.",
      source: "okrapdf/cli parsers/types.ts; desktop LocalProcessingProvider",
    },
    {
      id: "one_artifact_triple",
      summary:
        "A finished run exposes markdown + blocks + manifest. File names (doc.md vs result.md) are a client presentation choice.",
      source: "okrapdf/cli parse artifacts; desktop result.md/result.json/run.json",
    },
    {
      id: "event_driven_ui",
      summary:
        "Visible run state is rebuilt from page-lifecycle events. No stdout scraping, no per-surface hidden progress fields.",
      source: "OpenCode /event SSE; desktop ParserPageLifecycle",
    },
    {
      id: "bbox_converts_explicitly",
      summary:
        "Wire blocks use ParseBench [x1,y1,x2,y2] in 0–1000. 0–1 {x,y,w,h} and desktop {x,y,width,height} convert through the helpers in this module. Never mix units implicitly.",
      source: "okrapdf/cli core/blocks.ts; packages/schemas bbox.ts",
    },
    {
      id: "no_cloud_on_core_parse",
      summary:
        "The core parse path never requires an okraPDF account or a call to *.okrapdf.com. Cloud is `okra cloud` / S.5.7, opt-in.",
      source: "okrapdf/cli DESIGN.md cloud demotion + no-cloud guard",
    },
  ]);

// ---------------------------------------------------------------------------
// Capabilities a host advertises
// ---------------------------------------------------------------------------

export const clientCapabilitySchema = z.enum([
  "providers",
  "parsers",
  "documents",
  "parse",
  "runs.events",
  "runs.cancel",
  "runs.resume",
  "redact",
  "local_models",
]);
export type ClientCapability = z.infer<typeof clientCapabilitySchema>;

export const CLIENT_CAPABILITY_VOCABULARY: readonly ClientCapability[] =
  clientCapabilitySchema.options;

export const clientHealthSchema = z.object({
  object: z.literal("client_health"),
  protocol: z.literal(CLIENT_PROTOCOL_VERSION),
  healthy: z.literal(true),
  version: z.string().min(1),
  host: clientHostSchema,
  capabilities: z.array(clientCapabilitySchema),
});
export type ClientHealth = z.infer<typeof clientHealthSchema>;

// ---------------------------------------------------------------------------
// BBox — CLI 0–1000 is canonical on the wire
// ---------------------------------------------------------------------------

export const CANONICAL_BLOCK_LABELS = [
  "Caption",
  "Footnote",
  "Formula",
  "List-item",
  "Page-footer",
  "Page-header",
  "Picture",
  "Section-header",
  "Table",
  "Text",
  "Title",
] as const;

export type CanonicalBlockLabel = (typeof CANONICAL_BLOCK_LABELS)[number];

export const clientBlockLabelSchema = z.string().min(1);
export type ClientBlockLabel = z.infer<typeof clientBlockLabelSchema>;

/** ParseBench-native [x1, y1, x2, y2] in 0–1000, top-left origin. */
export const clientBboxSchema = z
  .tuple([
    z.number().min(0).max(1000),
    z.number().min(0).max(1000),
    z.number().min(0).max(1000),
    z.number().min(0).max(1000),
  ])
  .refine((box) => box[0] <= box[2] && box[1] <= box[3], {
    message: "client bbox must be [x1,y1,x2,y2] with x1<=x2 and y1<=y2",
  });
export type ClientBbox = z.infer<typeof clientBboxSchema>;

/** Desktop overlay box. 0–1 page fraction unless unit says otherwise. */
export const desktopBboxSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  unit: z.enum(["normalized", "px", "pt"]).default("normalized"),
  origin: z.literal("top-left").default("top-left"),
});
export type DesktopBbox = z.infer<typeof desktopBboxSchema>;

export function clientBboxToApi(box: ClientBbox): Bbox {
  const [x1, y1, x2, y2] = box;
  return {
    x: x1 / 1000,
    y: y1 / 1000,
    w: (x2 - x1) / 1000,
    h: (y2 - y1) / 1000,
  };
}

export function apiBboxToClient(box: Bbox): ClientBbox {
  const parsed = Schema.decodeUnknownSync(BboxCodec)(box);
  return [
    parsed.x * 1000,
    parsed.y * 1000,
    (parsed.x + parsed.w) * 1000,
    (parsed.y + parsed.h) * 1000,
  ];
}

export function clientBboxToDesktop(box: ClientBbox): DesktopBbox {
  const api = clientBboxToApi(box);
  return {
    x: api.x,
    y: api.y,
    width: api.w,
    height: api.h,
    unit: "normalized",
    origin: "top-left",
  };
}

export function desktopBboxToClient(box: DesktopBbox): ClientBbox {
  if (box.unit !== "normalized") {
    throw new Error(
      `desktop bbox unit "${box.unit}" cannot convert to client 0-1000 without page size`,
    );
  }
  return apiBboxToClient({
    x: box.x,
    y: box.y,
    w: box.width,
    h: box.height,
  });
}

// ---------------------------------------------------------------------------
// Providers + parsers (CLI seams)
// ---------------------------------------------------------------------------

export const clientProviderApiSchema = z.enum(["gemini", "openai-chat", "local"]);
export type ClientProviderApi = z.infer<typeof clientProviderApiSchema>;

export const clientKeySourceSchema = z.enum(["flag", "env", "config", "none"]);
export type ClientKeySource = z.infer<typeof clientKeySourceSchema>;

export const clientModelSpecSchema = z.object({
  id: z.string().min(1),
  displayName: z.string().min(1).optional(),
  vision: z.boolean(),
});
export type ClientModelSpec = z.infer<typeof clientModelSpecSchema>;

export const clientProviderSpecSchema = z.object({
  id: z.string().min(1),
  displayName: z.string().min(1),
  api: clientProviderApiSchema,
  envKeys: z.array(z.string().min(1)),
  baseUrl: z.string().min(1).optional(),
  baseUrlEnvKey: z.string().min(1).optional(),
  defaultModel: z.string().min(1).optional(),
  models: z.array(clientModelSpecSchema).default([]),
  keyHint: z.string().min(1),
  /** Local on-device providers (Apple Vision, Windows OCR, Dots, Chandra). */
  delivery: z.enum(["byok", "on_device", "loopback"]).default("byok"),
  /** Host-local readiness. Omitted by remote catalogs that resolve it later. */
  ready: z.boolean().optional(),
  status: z.string().min(1).optional(),
  isDefault: z.boolean().optional(),
});
export type ClientProviderSpec = z.infer<typeof clientProviderSpecSchema>;

export const clientProviderListSchema = z.object({
  object: z.literal("client_provider_list"),
  data: z.array(clientProviderSpecSchema),
});
export type ClientProviderList = z.infer<typeof clientProviderListSchema>;

export const clientParserRequiresSchema = z.enum(["vlm", "http", "none"]);
export type ClientParserRequires = z.infer<typeof clientParserRequiresSchema>;

export const clientParserSpecSchema = z.object({
  id: z.string().min(1),
  displayName: z.string().min(1),
  version: z.string().min(1),
  requires: clientParserRequiresSchema,
  defaults: z.record(z.string(), z.unknown()).default({}),
  ready: z.boolean().optional(),
  status: z.string().min(1).optional(),
  isDefault: z.boolean().optional(),
});
export type ClientParserSpec = z.infer<typeof clientParserSpecSchema>;

export const clientParserListSchema = z.object({
  object: z.literal("client_parser_list"),
  data: z.array(clientParserSpecSchema),
});
export type ClientParserList = z.infer<typeof clientParserListSchema>;

// ---------------------------------------------------------------------------
// Documents, runs, artifacts
// ---------------------------------------------------------------------------

export const clientDocumentSchema = z.object({
  object: z.literal("client_document"),
  id: z.string().min(1),
  title: z.string().min(1),
  /** Absolute local path, hosted document id, or other resolvable source. */
  source: z.string().min(1),
  pageCount: z.number().int().positive(),
  openedAt: z.string().min(1),
});
export type ClientDocument = z.infer<typeof clientDocumentSchema>;

export const clientDocumentListSchema = z.object({
  object: z.literal("client_document_list"),
  data: z.array(clientDocumentSchema),
});
export type ClientDocumentList = z.infer<typeof clientDocumentListSchema>;

export const clientTokenUsageSchema = z.object({
  inputTokens: z.number().nonnegative(),
  outputTokens: z.number().nonnegative(),
  thinkingTokens: z.number().nonnegative(),
});
export type ClientTokenUsage = z.infer<typeof clientTokenUsageSchema>;

export const EMPTY_CLIENT_USAGE: ClientTokenUsage = {
  inputTokens: 0,
  outputTokens: 0,
  thinkingTokens: 0,
};

export const clientLayoutBlockSchema = z.object({
  label: clientBlockLabelSchema,
  bbox: clientBboxSchema,
  text: z.string(),
  /** 1-indexed page number. */
  page: z.number().int().positive(),
});
export type ClientLayoutBlock = z.infer<typeof clientLayoutBlockSchema>;

export const clientPageParseSchema = z.object({
  page: z.number().int().positive(),
  markdown: z.string(),
  blocks: z.array(clientLayoutBlockSchema),
  usage: clientTokenUsageSchema.optional(),
});
export type ClientPageParse = z.infer<typeof clientPageParseSchema>;

export const clientParseManifestSchema = z.object({
  parserId: z.string().min(1),
  providerId: z.string().min(1).optional(),
  model: z.string().min(1).optional(),
  pageCount: z.number().int().nonnegative(),
  durationMs: z.number().nonnegative(),
  /** Omitted when the model is unpriced — never guessed. */
  costUsd: z.number().nonnegative().optional(),
  warnings: z.array(z.string()),
});
export type ClientParseManifest = z.infer<typeof clientParseManifestSchema>;

export const clientDocumentParseSchema = z.object({
  object: z.literal("client_document_parse"),
  markdown: z.string(),
  pages: z.array(clientPageParseSchema),
  blocks: z.array(clientLayoutBlockSchema),
  usage: clientTokenUsageSchema,
  meta: clientParseManifestSchema,
});
export type ClientDocumentParse = z.infer<typeof clientDocumentParseSchema>;

export const clientArtifactKindSchema = z.enum(["markdown", "blocks", "manifest"]);
export type ClientArtifactKind = z.infer<typeof clientArtifactKindSchema>;

export const CLIENT_ARTIFACT_FILES = {
  markdown: "doc.md",
  blocks: "blocks.json",
  manifest: "manifest.json",
} as const;

export const clientArtifactsSchema = z.object({
  markdown: z.string(),
  blocks: z.array(clientLayoutBlockSchema),
  manifest: clientParseManifestSchema,
});
export type ClientArtifacts = z.infer<typeof clientArtifactsSchema>;

export const clientRedactionCandidateSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  text: z.string(),
  score: z.number().min(0).max(1),
  source: z.string().min(1),
  /** 1-indexed page number. */
  page: z.number().int().positive(),
  bbox: clientBboxSchema,
});
export type ClientRedactionCandidate = z.infer<typeof clientRedactionCandidateSchema>;

export const clientRedactionEngineSchema = z.enum(["presidio", "basic-policy"]);
export type ClientRedactionEngine = z.infer<typeof clientRedactionEngineSchema>;

export const clientRedactionDetectionSchema = z.object({
  object: z.literal("client_redaction_detection"),
  runId: z.string().min(1),
  engine: clientRedactionEngineSchema,
  model: z.string().min(1).optional(),
  createdAt: z.string().min(1),
  candidates: z.array(clientRedactionCandidateSchema),
});
export type ClientRedactionDetection = z.infer<typeof clientRedactionDetectionSchema>;

export const clientRedactionStatusSchema = z.object({
  object: z.literal("client_redaction_status"),
  runId: z.string().min(1),
  status: z.literal("running"),
  engine: clientRedactionEngineSchema,
});
export type ClientRedactionStatus = z.infer<typeof clientRedactionStatusSchema>;

export function documentParseToArtifacts(parsed: ClientDocumentParse): ClientArtifacts {
  return {
    markdown: parsed.markdown,
    blocks: parsed.blocks,
    manifest: parsed.meta,
  };
}

// ---------------------------------------------------------------------------
// Page lifecycle — desktop's provider-neutral matrix
// ---------------------------------------------------------------------------

export const clientPageLifecycleStateSchema = z.enum([
  "idle",
  "in_progress",
  "done",
  "attention",
  "error",
]);
export type ClientPageLifecycleState = z.infer<typeof clientPageLifecycleStateSchema>;

const CLIENT_PAGE_LIFECYCLE_ALIASES: Record<string, ClientPageLifecycleState> = {
  idle: "idle",
  inProgress: "in_progress",
  in_progress: "in_progress",
  done: "done",
  attention: "attention",
  error: "error",
};

/** Accept desktop camelCase or the wire snake_case. */
export function normalizePageLifecycleState(state: string): ClientPageLifecycleState {
  const normalized = CLIENT_PAGE_LIFECYCLE_ALIASES[state];
  if (!normalized) {
    throw new Error(`unknown page lifecycle state: ${state}`);
  }
  return normalized;
}

export function canTransitionPageLifecycle(
  from: ClientPageLifecycleState,
  to: ClientPageLifecycleState,
): boolean {
  if (from === to) return true;
  if (to === "idle") return false;
  return from !== "done";
}

export function rollupPageLifecycle(
  states: readonly ClientPageLifecycleState[],
): ClientPageLifecycleState {
  if (states.includes("attention")) return "attention";
  if (states.includes("error")) return "error";
  if (states.includes("in_progress")) return "in_progress";
  if (states.length > 0 && states.every((state) => state === "done")) {
    return "done";
  }
  return "idle";
}

export const clientPageLifecycleSchema = z.object({
  parserId: z.string().min(1),
  page: z.number().int().positive(),
  state: clientPageLifecycleStateSchema,
  detail: z.string().optional(),
  updatedAt: z.string().min(1),
});
export type ClientPageLifecycle = z.infer<typeof clientPageLifecycleSchema>;

export const clientRunStatusSchema = z.enum([
  "queued",
  "running",
  "cancelling",
  "cancelled",
  "succeeded",
  "failed",
  "attention",
]);
export type ClientRunStatus = z.infer<typeof clientRunStatusSchema>;

export const clientRunSchema = z.object({
  object: z.literal("client_run"),
  id: z.string().min(1),
  documentId: z.string().min(1),
  parserId: z.string().min(1),
  providerId: z.string().min(1).optional(),
  model: z.string().min(1).optional(),
  status: clientRunStatusSchema,
  pageCount: z.number().int().nonnegative(),
  pages: z.array(clientPageLifecycleSchema),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});
export type ClientRun = z.infer<typeof clientRunSchema>;

export const clientRunListSchema = z.object({
  object: z.literal("client_run_list"),
  data: z.array(clientRunSchema),
});
export type ClientRunList = z.infer<typeof clientRunListSchema>;

export const clientParseRequestSchema = z.object({
  parserId: z.string().min(1).default("layout-vlm"),
  providerId: z.string().min(1).optional(),
  model: z.string().min(1).optional(),
  pages: z
    .object({
      from: z.number().int().positive(),
      to: z.number().int().positive(),
    })
    .optional(),
  concurrency: z.number().int().positive().optional(),
  allowEmpty: z.boolean().optional(),
});
export type ClientParseRequest = z.infer<typeof clientParseRequestSchema>;

// ---------------------------------------------------------------------------
// Events — OpenCode SSE, okra nouns
// ---------------------------------------------------------------------------

export const clientProtocolEventTypeSchema = z.enum([
  "server.connected",
  "document.opened",
  "run.started",
  "run.page",
  "run.completed",
  "run.failed",
  "run.cancelled",
  "provider.setup",
]);
export type ClientProtocolEventType = z.infer<typeof clientProtocolEventTypeSchema>;

const clientEventBaseSchema = z.object({
  protocol: z.literal(CLIENT_PROTOCOL_VERSION),
  id: z.string().min(1),
  seq: z.number().int().nonnegative(),
  createdAt: z.string().min(1),
});

export const clientProtocolEventSchema = z.discriminatedUnion("type", [
  clientEventBaseSchema.extend({
    type: z.literal("server.connected"),
    host: clientHostSchema,
  }),
  clientEventBaseSchema.extend({
    type: z.literal("document.opened"),
    document: clientDocumentSchema,
  }),
  clientEventBaseSchema.extend({
    type: z.literal("run.started"),
    run: clientRunSchema,
  }),
  clientEventBaseSchema.extend({
    type: z.literal("run.page"),
    runId: z.string().min(1),
    page: clientPageLifecycleSchema,
    parse: clientPageParseSchema.optional(),
  }),
  clientEventBaseSchema.extend({
    type: z.literal("run.completed"),
    run: clientRunSchema,
    artifacts: clientArtifactsSchema,
  }),
  clientEventBaseSchema.extend({
    type: z.literal("run.failed"),
    runId: z.string().min(1),
    page: z.number().int().positive().optional(),
    message: z.string().min(1),
  }),
  clientEventBaseSchema.extend({
    type: z.literal("run.cancelled"),
    runId: z.string().min(1),
  }),
  clientEventBaseSchema.extend({
    type: z.literal("provider.setup"),
    providerId: z.string().min(1),
    phase: z.string().min(1),
    detail: z.string().optional(),
  }),
]);
export type ClientProtocolEvent = z.infer<typeof clientProtocolEventSchema>;

// ---------------------------------------------------------------------------
// HTTP + SSE catalog (OpenCode-shaped, okra nouns)
// ---------------------------------------------------------------------------

export const clientEndpointMethodSchema = z.enum(["GET", "POST", "PATCH", "DELETE"]);

export const CLIENT_PROTOCOL_ENDPOINTS = z
  .array(
    z.object({
      id: z.string().min(1),
      method: clientEndpointMethodSchema,
      path: z.string().min(1),
      capability: clientCapabilitySchema.optional(),
      summary: z.string().min(1),
      desktopWindowsToday: z.string().min(1).optional(),
    }),
  )
  .parse([
    {
      id: "global.health",
      method: "GET",
      path: "/global/health",
      summary: "Health, protocol version, host, and advertised capabilities.",
      desktopWindowsToday: "GET /api/health",
    },
    {
      id: "global.event",
      method: "GET",
      path: "/global/event",
      capability: "runs.events",
      summary: "SSE bus. First event is server.connected, then document/run events.",
    },
    {
      id: "provider.list",
      method: "GET",
      path: "/provider",
      capability: "providers",
      summary: "Provider catalog + which ones have resolved credentials.",
      desktopWindowsToday: "GET /api/providers",
    },
    {
      id: "parser.list",
      method: "GET",
      path: "/parser",
      capability: "parsers",
      summary: "Parser seam catalog. layout-vlm is a peer, not a privileged core.",
    },
    {
      id: "document.create",
      method: "POST",
      path: "/document",
      capability: "documents",
      summary: "Register/open a PDF. Must not start a parse.",
      desktopWindowsToday: "POST /api/document/select",
    },
    {
      id: "document.list",
      method: "GET",
      path: "/document",
      capability: "documents",
      summary: "Open documents in this host session.",
    },
    {
      id: "document.get",
      method: "GET",
      path: "/document/:id",
      capability: "documents",
      summary: "One open document. No parse side effects.",
      desktopWindowsToday: "GET /api/document/current",
    },
    {
      id: "document.parse",
      method: "POST",
      path: "/document/:id/parse",
      capability: "parse",
      summary: "The only verb that starts a run. Body is ClientParseRequest.",
      desktopWindowsToday: "POST /api/runs",
    },
    {
      id: "run.list",
      method: "GET",
      path: "/run",
      capability: "parse",
      summary: "Parse runs for the host session.",
      desktopWindowsToday: "GET /api/runs",
    },
    {
      id: "run.get",
      method: "GET",
      path: "/run/:id",
      capability: "parse",
      summary: "Run snapshot including the page-lifecycle matrix.",
      desktopWindowsToday: "GET /api/runs/{id}",
    },
    {
      id: "run.events",
      method: "GET",
      path: "/run/:id/events",
      capability: "runs.events",
      summary: "SSE attach + replay for one run.",
    },
    {
      id: "run.artifacts",
      method: "GET",
      path: "/run/:id/artifacts",
      capability: "parse",
      summary: "Finished markdown + blocks + manifest.",
      desktopWindowsToday: "GET /api/runs/{id}/output",
    },
    {
      id: "run.pii.analyze",
      method: "POST",
      path: "/run/:id/pii/analyze",
      capability: "redact",
      summary: "Explicitly analyze a completed positioned parse for PII.",
    },
    {
      id: "run.pii.analysis.get",
      method: "GET",
      path: "/run/:id/pii/analysis",
      capability: "redact",
      summary: "Persisted PII analysis status or source-aligned redaction candidates.",
    },
    {
      id: "run.cancel",
      method: "POST",
      path: "/run/:id/cancel",
      capability: "runs.cancel",
      summary: "Cancel an in-flight run. Completed pages stay durable.",
      desktopWindowsToday: "POST /api/runs/{id}/cancel",
    },
    {
      id: "run.resume",
      method: "POST",
      path: "/run/:id/resume",
      capability: "runs.resume",
      summary: "Resume from durable page checkpoints.",
      desktopWindowsToday: "POST /api/runs/{id}/resume",
    },
  ]);

export const CLIENT_PROTOCOL_S5_7_PROJECTION = {
  "GET /global/health": "GET /v1/meta (protocol + capabilities subset)",
  "POST /document": "POST /v1/documents or POST /v1/upload",
  "POST /document/:id/parse": "POST /v1/parse or POST /v1/jobs {type: document.parse}",
  "GET /run/:id": "GET /v1/jobs/:id",
  "GET /run/:id/events": "GET /v1/documents/:id/events",
} as const;
